import React, { useEffect, useMemo } from 'react';
import { BsPlus, BsStar, BsStarFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, Icon, Text } from '@chakra-ui/react';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../../../constants/inAppText';
import { Statuses } from '../../../../../../../../constants/statuses';
import { TabNames } from '../../../../../../../../constants/tabMenuTypes';
import { selectCompanyId } from '../../../../../../../../redux/modules/company';
import { hideTray, showTray } from '../../../../../../../../redux/modules/tray';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import SearchInputGroup from '../../../../../../../SearchInputGroup';
import { customDocumentOptions, REQUIREMENT_TYPES } from '../../../../../../util/options';
import { getCreateRequirementTemplateBlockParams } from '../../../../../../util/util';
import { RequirementMenuItem, RequirementPropertyTypeMenu } from '../../../../../RequirementPropertyTypeMenu';
import RequirementTypesFilters from '../../../../../RequirementTypesFilters';

import CreateCustomDocumentTypeTray from './components/CreateCustomDocumentTypeTray';

const TemplateBuilderDocumentPanel = ({ blueprint }) => {
  const dispatch = useDispatch();
  const companyId = useSelector(selectCompanyId);
  const currentTemplate = useSelector(blueprint.selectCurrentRequirementTemplate);

  const currentRequirementTemplateBlocks = useSelector(blueprint.selectCurrentRequirementTemplateBlocks);
  let currentMaxOrder = 0;

  useEffect(() => {
    if (currentRequirementTemplateBlocks) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      currentMaxOrder = Math.max(...currentRequirementTemplateBlocks.map(templateBlock => templateBlock.order));
    }
  }, [currentRequirementTemplateBlocks, dispatch]);

  useEffect(() => {
    if (companyId && currentTemplate) {
      dispatch(blueprint.listInsuranceDocumentTypes());
    }
  }, [companyId, currentTemplate?.id, dispatch]);

  const insuranceDocumentTypes = useSelector(blueprint.selectInsuranceDocumentTypes);

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    dispatch(blueprint.updateInsuranceDocumentTypeSearchFilter(searchValue));
  };

  const handleFilterChange = (isChecked, type) => {
    dispatch(blueprint.setInsuranceDocumentTypeFilters({ ...insuranceDocumentTypes.filters, [type]: isChecked }));
  };

  const showCreateCustomDocumentTypeTray = () => {
    dispatch(blueprint.setCreateCustomStep(0, TabNames.Documents));
    dispatch(showTray(
      () => <CreateCustomDocumentTypeTray blueprint={blueprint} />,
      {
        onClose: () => {
          dispatch(blueprint.setEditCustomDocumentId(null));
          dispatch(hideTray());
        }
      }
    ));
  };

  const insuranceDocumentTypeTabs = useMemo(() => insuranceDocumentTypes?.data.map(documentTypeData => ({
    id: documentTypeData?.id,
    tabId: documentTypeData?.name,
    title: documentTypeData?.displayName,
    description: documentTypeData?.description,
    icon: documentTypeData?.favorite ? BsStarFill : BsStar,
    isFavorite: documentTypeData?.favorite ?? 0,
    onClick: () => {
      if (currentTemplate?.publishedAt) dispatch(blueprint.updateChangesPublishedStatus(Statuses.Updating));
      dispatch(blueprint.createRequirementTemplateBlock(getCreateRequirementTemplateBlockParams(
        currentMaxOrder,
        REQUIREMENT_TYPES.Documents,
        documentTypeData
      )));
    },
    onFavoriteButtonCLick: () => (documentTypeData?.favorite
      ? dispatch(blueprint.removeDocumentTypeFavorite(documentTypeData?.id))
      : dispatch(blueprint.addDocumentTypeFavorite(documentTypeData?.id))),
    submenu: documentTypeData?.companyId ? customDocumentOptions : null
  })), [currentMaxOrder, dispatch, insuranceDocumentTypes.data]);

  return (
    <Flex direction="column" h="100%" justify="flex-start" w="100%">
      <Box mt={4} px={5} py={4}>
        <Text color={colors.greyScale['500']} textAlign="left" textStyle="bodySm">
          {inAppText.templatesPage.templateBuilder.documentsPane.addDocumentInstructions}
        </Text>
        <Button
          leftIcon={<Icon as={BsPlus} />}
          mt={4}
          variant="secondary"
          w="100%"
          onClick={showCreateCustomDocumentTypeTray}
        >
          Create Custom Document
        </Button>
        <HStack justify="space-around" mt={4} spacing={1} width="100%">
          <SearchInputGroup h="40px" onChange={handleSearchChange} />
          <RequirementTypesFilters
            blueprint={blueprint}
            currentFilters={insuranceDocumentTypes.filters}
            type="Documents"
            onChange={handleFilterChange}
            onReset={() => dispatch(blueprint.listInsuranceDocumentTypes())}
          />
        </HStack>
      </Box>
      {insuranceDocumentTypeTabs?.length > 0
      && (
        <RequirementPropertyTypeMenu
          blueprint={blueprint}
          cypressIdFunction={CypressIds.docTypeMenuOption}
          fetchNext={() => dispatch(blueprint.listInsuranceDocumentTypes({ pageNumber: insuranceDocumentTypes.currentPage + 1, pageSize: 25 }))}
          hasMore={insuranceDocumentTypes?.currentPage !== insuranceDocumentTypes?.lastPage}
        >
          {insuranceDocumentTypeTabs.map(option => (
            <RequirementMenuItem
              {...option}
              key={option.id}
              subMenu={option?.submenu}
            />
          ))}
        </RequirementPropertyTypeMenu>
      )}
    </Flex>
  );
};

TemplateBuilderDocumentPanel.propTypes = { blueprint: RequirementTemplateBlueprintType };

export default TemplateBuilderDocumentPanel;
